import { useState } from 'react';

import { Box, Button, CloseButton, Text } from '@opendoor/bricks-next';
import { SearchFilters } from 'components/marketplace/search/SearchFilters';
import type { SearchFiltersProps } from 'components/marketplace/search/SearchFilters';

interface MapSearchFiltersProps extends SearchFiltersProps {
  onClose?: () => void;
  onApplyFilters?: () => void;
  onResetFilters?: () => void;
  isLoading: boolean;
}

export const MapSearchFilters = (props: MapSearchFiltersProps) => {
  // Dropdown in bricks-next only reads prefilledValue on mount (uncontrolled).
  // We use a key to force re-render when filters reset OR when QuickFilters
  // updates bed/bath values externally.
  const [resetFiltersKey, setResetFiltersKey] = useState(0);
  const handleResetFilters = () => {
    setResetFiltersKey((prev) => prev + 1);
    props.onResetFilters?.();
  };

  const searchDisabled = props?.errors ? Object.keys(props.errors).length > 0 : false;

  return (
    <Box
      width="100%"
      flexDirection="column"
      height="100%"
      backgroundColor="$backgroundPrimary"
      style={{
        overflowY: 'scroll',
        overflowX: 'hidden',
      }}
      data-testid="map-search-filters"
    >
      <Box
        flexDirection="row"
        justifyContent="space-between"
        alignItems="baseline"
        my={12}
        mx={36}
        $smallerThanLG={{ mx: 12 }}
      >
        <Text tag="p" typography="$labelLarge" mb={24} $largerThanMD={{ display: 'none' }}>
          Filters
        </Text>
        <CloseButton
          analyticsName="close-property-search-filters"
          data-testid="close-property-search-filters"
          onPress={props.onClose}
          size="large"
          variant="default"
          p={10}
          $largerThanMD={{ p: 0, ml: -10 }}
        />
      </Box>
      <Box mt={24} mb={12} mx={36} $smallerThanLG={{ mx: 24 }}>
        <SearchFilters {...props} key={`${resetFiltersKey}-${props.minBeds}-${props.minBaths}`} />
      </Box>
      <Box
        flexDirection="row"
        justifyContent="flex-end"
        alignItems="center"
        $smallerThanMD={{ justifyContent: 'space-between' }}
        backgroundColor="$backgroundPrimary"
        bottom={0}
        gap={8}
        width="100%"
        px={24}
        py={12}
        zIndex={1}
        style={{
          position: 'sticky',
        }}
      >
        <Button
          analyticsName="property-search-filters-reset"
          label="Reset filters"
          onPress={handleResetFilters}
          size="medium"
          variant="secondary"
          minWidth={150}
          $smallerThanMD={{ size: 'small' }}
        />
        <Button
          analyticsName="property-search-filters"
          label="Search"
          onPress={props.onApplyFilters}
          size="medium"
          variant="primary"
          loading={props.isLoading}
          minWidth={150}
          $smallerThanMD={{ flex: 1, size: 'small' }}
          disabled={searchDisabled}
        />
      </Box>
    </Box>
  );
};
